import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  IconButton,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
// import DeleteIcon from "@mui/icons-material/Delete";

export interface Column<T = any> {
  header: string;
  key: string;
  editable?: boolean;
  options?: string[];
  render?: (value: any, row: T) => React.ReactNode;
}

interface EditableDisplayTableProps {
  columns: Column[];
  data: any[];
  loading?: boolean;
  isEditable?: boolean;
  onDataChange?: (updatedData: any[]) => void;
}

function useDebounce(value: any, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const EditableCell = React.memo(
  ({
    value,
    onChange,
    isEditable,
    renderFunction,
  }: {
    value: any;
    onChange: (newValue: any) => void;
    isEditable: boolean;
    renderFunction: any;
  }) => {
    const [internalValue, setInternalValue] = useState(value);

    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    const debouncedValue = useDebounce(internalValue, 400);

    const isInitialMount = useRef(true);
    useEffect(() => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
      } else {
        if (debouncedValue !== value) {
          onChange(debouncedValue);
        }
      }
    }, [debouncedValue, onChange, value]);

    if (!isEditable) {
      return renderFunction ? renderFunction(value) : <>{value}</>;
    }

    return (
      <TextField
        fullWidth
        size="small"
        value={internalValue ?? ""}
        onChange={(e) => setInternalValue(e.target.value)}
        variant="outlined"
      />
    );
  }
);

const EditableDisplayTable: React.FC<EditableDisplayTableProps> = ({
  columns,
  data,
  loading = false,
  isEditable = false,
  onDataChange,
}) => {
  const initialData = useMemo(() => [...data], [data]);
  const [tableData, setTableData] = useState(initialData);

  useEffect(() => {
    if (!isEditable) {
      setTableData([...data]);
    }
  }, [data, isEditable]);

  const getNestedValue = useCallback((obj: any, key: string): any => {
    const keys = key.split(".");
    return keys.reduce((acc, k) => acc?.[k], obj);
  }, []);

  const handleValueChange = useCallback(
    (rowIndex: number, key: string, value: any) => {
      setTableData((prevData) => {
        const newData = [...prevData];

        const keys = key.split(".");
        let current = { ...newData[rowIndex] };
        newData[rowIndex] = current;

        for (let i = 0; i < keys.length - 1; i++) {
          current[keys[i]] = { ...current[keys[i]] };
          current = current[keys[i]];
        }

        current[keys[keys.length - 1]] = value;

        if (onDataChange) {
          onDataChange(newData);
        }

        return newData;
      });
    },
    [onDataChange]
  );

  const handleAddRow = useCallback(() => {
    const newRow = columns.reduce((acc, column) => {
      const keys = column.key.split(".");
      let current = acc;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = "";

      return acc;
    }, {} as any);

    setTableData((prevData) => {
      const newData = [...prevData, newRow];

      if (onDataChange) {
        onDataChange(newData);
      }

      return newData;
    });
  }, [columns, onDataChange]);

  // const handleDeleteRow = useCallback((rowIndex: number) => {
  //   setTableData(prevData => {
  //     const newData = prevData.filter((_, index) => index !== rowIndex);

  //     if (onDataChange) {
  //       onDataChange(newData);
  //     }

  //     return newData;
  //   });
  // }, [onDataChange]);

  const tableContent = useMemo(() => {
    if (loading) {
      return (
        <TableRow>
          <TableCell
            colSpan={columns.length + (isEditable ? 1 : 0)}
            align="center"
          >
            <CircularProgress size={24} />
          </TableCell>
        </TableRow>
      );
    }

    if (tableData.length === 0) {
      return (
        <TableRow>
          <TableCell
            colSpan={columns.length + (isEditable ? 1 : 0)}
            align="center"
          >
            No data available.
          </TableCell>
        </TableRow>
      );
    }

    return tableData.map((row, rowIndex) => (
      <TableRow key={rowIndex}>
        {columns.map((col, colIndex) => {
          const value = getNestedValue(row, col.key);

          return (
            <TableCell key={colIndex}>
              <EditableCell
                value={value}
                onChange={(newValue: any) =>
                  handleValueChange(rowIndex, col.key, newValue)
                }
                isEditable={isEditable && col.editable !== false}
                renderFunction={(val: any) =>
                  col.render ? col.render(val, row) : val
                }
              />
            </TableCell>
          );
        })}

        {/* {isEditable && (
          <TableCell align="right">
            <IconButton 
              size="small" 
              color="error" 
              onClick={() => handleDeleteRow(rowIndex)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </TableCell>
        )} */}
      </TableRow>
    ));
  }, [
    columns,
    tableData,
    isEditable,
    loading,
    getNestedValue,
    handleValueChange,
    // handleDeleteRow
  ]);

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((col, index) => (
              <TableCell key={index}>{col.header}</TableCell>
            ))}
            {/* {isEditable && (
              <TableCell align="right">Actions</TableCell>
            )} */}
          </TableRow>
        </TableHead>

        <TableBody>
          {tableContent}

          {isEditable && (
            <TableRow>
              <TableCell colSpan={columns.length + 1} align="center">
                <Box sx={{ my: 1 }}>
                  <IconButton
                    color="primary"
                    onClick={handleAddRow}
                    size="small"
                  >
                    <AddIcon /> Add Row
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default React.memo(EditableDisplayTable);
